import React, { useState } from "react";
import { Box, Typography, IconButton, Stack } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/system";
import { allowedFileTypes } from "../../../constants/enum";
import { useTranslate } from "../../../locales/use-locales";

const DropBox = styled(Box)({
  boxSizing: "border-box",
  // border: "2px dashed #cccccc",
  borderRadius: 8,
  width: "100%",
  padding: 50,
  textAlign: "center",
  cursor: "pointer",
  transition: "background-color 0.2s, border-color 0.2s",
});

const Thumbnail = styled(Box)({
  position: "relative",
  width: 100,
  height: 100,
  margin: 8,
  border: "1px solid #ccc",
  borderRadius: 8,
  overflow: "hidden",
  backgroundColor: "#f9f9f9",
  "& img": { width: "100%", height: "100%", objectFit: "cover" },
});

const RemoveButton = styled(IconButton)({
  position: "absolute",
  top: 4,
  right: 4,
  backgroundColor: "rgba(255,255,255,0.8)",
});

const FileUpload = ({
  allowMultiple = false,
  onRemoveFile,
  onFileChange,
  title,
  id,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslate();
  const allowedTypes = allowedFileTypes;

  const handleFiles = (filesArray) => {
    setError("");

    // If multiple files are dragged but multiple files are not allowed, only take the first file
    const filesToProcess = allowMultiple ? filesArray : [filesArray[0]];

    const invalidFiles = filesToProcess.filter(
      (file) => !allowedTypes.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      setError("Endast PDF, PNG, JPEG, GIF, DOCX och WebP filer är tillåtna");
      return;
    }

    const previews = filesToProcess.map((file) => ({
      file,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : file.type === "application/pdf"
        ? "/assets/pdf.png"
        : null,
    }));
    setSelectedFiles(previews);
    onFileChange(filesToProcess);
  };

  const handleFileChange = (e) => handleFiles(Array.from(e.target.files));

  const handleRemove = (idx) => {
    const updated = selectedFiles.filter((_, i) => i !== idx);
    setSelectedFiles(updated);
    onRemoveFile();
  };

  const dragEvents = {
    onDragEnter: (e) => {
      e.preventDefault();
      setIsDragging(true);
    },
    onDragLeave: (e) => {
      e.preventDefault();
      setIsDragging(false);
    },
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(Array.from(e.dataTransfer.files));
    },
  };

  return (
    <Stack gap={2} width="100%">
      <Typography variant="subtitle1" color="white" fontWeight="bold">
        {title}
      </Typography>

      <DropBox
        {...dragEvents}
        sx={{
          borderColor: isDragging ? "primary.main" : undefined,
          backgroundColor: "#f9f9f9",
        }}
      >
        <input
          id={id}
          type="file"
          multiple={allowMultiple}
          accept=".pdf,.png,.jpeg,.jpg,.gif,.webp,.docx"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <label htmlFor={id} style={{ cursor: "pointer" }}>
          <CloudUploadIcon fontSize="large" />
          <Typography variant="h6">
            {t("form.fileUpload.dragOrSelect", { allowMultiple })}
          </Typography>
          <Typography variant="body2">
            {t("form.fileUpload.dropFilesHere")}
          </Typography>
        </label>
      </DropBox>

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {selectedFiles.length > 0 && (
        <Box display="flex" flexWrap="wrap">
          {selectedFiles.map((fileObj, i) => (
            <Thumbnail key={i}>
              {fileObj.preview ? (
                <img src={fileObj.preview} alt={fileObj.file.name} />
              ) : (
                <Typography variant="caption">{fileObj.file.name}</Typography>
              )}
              <RemoveButton size="small" onClick={() => handleRemove(i)}>
                <CloseIcon fontSize="small" />
              </RemoveButton>
            </Thumbnail>
          ))}
        </Box>
      )}
    </Stack>
  );
};

export default FileUpload;
